import { useState, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Clock, Pencil, Check, X } from "lucide-react";

/**
 * Unified court availability component.
 * Props:
 *   courts            : { "2026-06-01": 2, "2026-06-02": 3, ... }
 *   onCourtsChange    : (newCourts) => void
 *   courtNames        : { "2026-06-01": { 1: "Campo 13", 2: "Campo A" }, ... }  ← per-day names
 *   onCourtNamesChange: (newNames) => void
 *   availability      : { court_1: { "2026-06-01": [{from,to}] } }
 *   onChange          : (newAvailability) => void
 *   defaultAvailability  : { "2026-06-01": [{from,to}] } — applied to ALL courts on that date
 *   onDefaultChange      : (newDefaultAvailability) => void
 *   globalAvailability   : [{from,to}] — applied to EVERY day and EVERY court
 *   onGlobalChange       : (newGlobalAvailability) => void
 *   startDate, endDate
 *
 *   Hierarchy when resolving availability for (court, date):
 *     court_{N}[date]  >  defaultAvailability[date]  >  globalAvailability  >  all-day
 */
export const CourtAvailability = ({
  courts = {},
  onCourtsChange,
  courtNames = {},
  onCourtNamesChange,
  availability = {},
  onChange,
  defaultAvailability = {},
  onDefaultChange,
  globalAvailability = [],
  onGlobalChange,
  startDate,
  endDate,
}) => {
  // openSlot.court values:
  //  -1 = global default for all days + all courts
  //   0 = day-level default for all courts on dateStr
  //  >0 = specific court number on dateStr
  const [openSlot, setOpenSlot]     = useState(null); // { court, dateStr }
  const [newFrom, setNewFrom]       = useState("09:00");
  const [newTo, setNewTo]           = useState("10:00");
  // Inline name editing: { dateStr, court } | null
  const [editingKey, setEditingKey] = useState(null);
  const [editVal, setEditVal]       = useState("");
  const nameInputRef                = useRef(null);

  const tournamentDates = useMemo(() => {
    if (!startDate || !endDate) return [];
    const dates = [];
    // Use T12:00:00 (noon) to avoid UTC timezone off-by-one: midnight local time
    // in UTC+1/+2 becomes the previous day when converted via toISOString().
    const current = new Date(startDate + "T12:00:00");
    const end     = new Date(endDate   + "T12:00:00");
    while (current <= end) {
      dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }, [startDate, endDate]);

  // ── helpers ──────────────────────────────────────────────────────────────
  const courtLabel = (dateStr, num) => {
    const dayNames = courtNames[dateStr] || {};
    return (dayNames[num] || "").trim() || `Campo ${num}`;
  };

  const getSlots = (court, dateStr) => {
    if (court === -1) return globalAvailability || [];
    if (court === 0) return defaultAvailability[dateStr] || [];
    const d = availability[`court_${court}`];
    return (d && d[dateStr]) || [];
  };

  const formatDate = (dateStr) => {
    const d      = new Date(dateStr + "T00:00:00");
    const days   = ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];
    const months = ["gen","feb","mar","apr","mag","giu","lug","ago","set","ott","nov","dic"];
    return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
  };

  // ── court name inline editing ─────────────────────────────────────────────
  const startEditName = (dateStr, num, e) => {
    e.stopPropagation();
    setEditingKey({ dateStr, num });
    setEditVal(courtLabel(dateStr, num));
    setTimeout(() => nameInputRef.current?.focus(), 50);
  };

  const confirmEditName = () => {
    if (!editingKey) return;
    const { dateStr, num } = editingKey;
    const dayNames = { ...(courtNames[dateStr] || {}) };
    dayNames[num]  = editVal.trim() || `Campo ${num}`;
    onCourtNamesChange({ ...courtNames, [dateStr]: dayNames });
    setEditingKey(null);
  };

  const cancelEditName = () => setEditingKey(null);

  // ── slot add / remove ─────────────────────────────────────────────────────
  const addSlot = () => {
    if (!openSlot || newFrom >= newTo) return;
    const { court, dateStr } = openSlot;
    const current = getSlots(court, dateStr);
    if (current.some(s => newFrom < s.to && newTo > s.from)) return;
    const updated = [...current, { from: newFrom, to: newTo }]
      .sort((a, b) => a.from.localeCompare(b.from));
    if (court === -1) {
      onGlobalChange && onGlobalChange(updated);
    } else if (court === 0) {
      onDefaultChange && onDefaultChange({ ...defaultAvailability, [dateStr]: updated });
    } else {
      const key = `court_${court}`;
      onChange({ ...availability, [key]: { ...(availability[key] || {}), [dateStr]: updated } });
    }
    setNewFrom("09:00");
    setNewTo("10:00");
  };

  const removeSlot = (court, dateStr, idx) => {
    const updated  = [...getSlots(court, dateStr)];
    updated.splice(idx, 1);
    if (court === -1) {
      onGlobalChange && onGlobalChange(updated);
      return;
    }
    if (court === 0) {
      const nextDefault = { ...defaultAvailability };
      if (updated.length === 0) delete nextDefault[dateStr];
      else nextDefault[dateStr] = updated;
      onDefaultChange && onDefaultChange(nextDefault);
      return;
    }
    const key      = `court_${court}`;
    const courtData = { ...(availability[key] || {}) };
    if (updated.length === 0) delete courtData[dateStr];
    else courtData[dateStr] = updated;
    onChange({ ...availability, [key]: Object.keys(courtData).length ? courtData : undefined });
  };

  if (tournamentDates.length === 0) {
    return (
      <p className="text-xs text-amber-600 py-2">
        Imposta le date di inizio e fine torneo per configurare gli orari disponibili dei campi.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="court-availability-panel">
      {/* ── Global default banner (all days + all courts) ─────────────── */}
      {onGlobalChange && (() => {
        const globalSlots = globalAvailability || [];
        const hasGlobal = globalSlots.length > 0;
        return (
          <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2
            ${hasGlobal ? "border-purple-300 bg-purple-50" : "border-dashed border-slate-300 bg-slate-50"}`}>
            <div className="flex-1">
              <p className={`text-sm font-bold uppercase tracking-wide ${hasGlobal ? "text-purple-800" : "text-slate-600"}`}>
                Disponibilità globale (tutti i giorni, tutti i campi)
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Fasce di default applicate a <strong>tutti</strong> i campi di <strong>tutti</strong> i giorni.
                Può essere sovrascritta a livello di singolo giorno (Default) o di singolo campo.
              </p>
            </div>
            <button
              type="button"
              data-testid="global-avail-btn"
              onClick={() => { setOpenSlot({ court: -1, dateStr: null }); setNewFrom("09:00"); setNewTo("21:00"); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border-2 transition-colors hover:brightness-95 flex-shrink-0
                ${hasGlobal
                  ? "border-purple-400 bg-purple-100 text-purple-800"
                  : "border-slate-300 bg-white text-slate-600"}`}
            >
              <Clock className="h-3.5 w-3.5" />
              <span>{hasGlobal ? `${globalSlots.length} fascia/e` : "Imposta"}</span>
            </button>
          </div>
        );
      })()}
      {tournamentDates.map((dateStr) => {
        const numCourts = parseInt(courts[dateStr]) || 0;
        const courtNums = numCourts > 0 ? Array.from({ length: numCourts }, (_, i) => i + 1) : [];

        return (
          <div key={dateStr} className="border border-slate-200 rounded-lg overflow-hidden">
            {/* ── Day header ── */}
            <div className="flex items-center gap-3 px-4 py-3 bg-slate-50">
              <span className="text-sm font-semibold text-slate-700 w-28 flex-shrink-0 capitalize">
                {formatDate(dateStr)}
              </span>
              <Input
                data-testid={`edit-courts-${dateStr}`}
                type="number" min="0" max="20"
                value={courts[dateStr] ?? 0}
                onChange={(e) =>
                  onCourtsChange({ ...courts, [dateStr]: parseInt(e.target.value) || 0 })
                }
                className="w-16 text-center rounded-md h-8 text-sm"
              />
              <span className="text-xs text-slate-500">campi disponibili</span>
              {numCourts > 0 && onDefaultChange && (() => {
                const defSlots = defaultAvailability[dateStr] || [];
                const hasDef = defSlots.length > 0;
                return (
                  <button
                    type="button"
                    data-testid={`default-avail-btn-${dateStr}`}
                    onClick={() => { setOpenSlot({ court: 0, dateStr }); setNewFrom("09:00"); setNewTo("21:00"); }}
                    className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border-2 transition-colors hover:brightness-95
                      ${hasDef
                        ? "border-blue-300 bg-blue-50 text-blue-800"
                        : "border-slate-200 bg-white text-slate-500"}`}
                  >
                    <Clock className="h-3.5 w-3.5" />
                    <span>Default tutti i campi</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full
                      ${hasDef ? "bg-blue-200 text-blue-700" : "bg-slate-200 text-slate-500"}`}>
                      {hasDef ? `${defSlots.length} fascia/e` : "tutto il giorno"}
                    </span>
                  </button>
                );
              })()}
            </div>

            {/* ── Court buttons with inline name edit ── */}
            {courtNums.length > 0 && (
              <div className="flex flex-wrap gap-2 px-4 py-3 bg-white border-t border-slate-100">
                {courtNums.map((num) => {
                  const slots    = getSlots(num, dateStr);
                  const hasSlots = slots.length > 0;
                  const hasDefault = (defaultAvailability[dateStr] || []).length > 0;
                  const hasGlobal = (globalAvailability || []).length > 0;
                  const inheritsFrom = hasDefault ? "default" : (hasGlobal ? "global" : null);
                  const isEditing = editingKey?.dateStr === dateStr && editingKey?.num === num;

                  return (
                    <div key={num} className={`flex items-center rounded-lg border-2 overflow-hidden
                      ${hasSlots
                        ? "border-emerald-300 bg-emerald-50"
                        : (inheritsFrom === "default"
                            ? "border-blue-200 bg-blue-50/50"
                            : (inheritsFrom === "global"
                                ? "border-purple-200 bg-purple-50/50"
                                : "border-slate-200 bg-slate-50"))
                      }`}
                    >
                      {/* Name area — click pencil to edit, click label to open availability */}
                      {isEditing ? (
                        <div className="flex items-center gap-1 px-2 py-1">
                          <input
                            ref={nameInputRef}
                            data-testid={`court-name-input-${num}-${dateStr}`}
                            value={editVal}
                            onChange={e => setEditVal(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") confirmEditName(); if (e.key === "Escape") cancelEditName(); }}
                            className="w-28 h-6 text-xs border border-slate-300 rounded px-1.5 outline-none focus:ring-1 focus:ring-emerald-400"
                            placeholder={`Campo ${num}`}
                          />
                          <button type="button" onClick={confirmEditName} className="text-emerald-600 hover:text-emerald-700 p-0.5">
                            <Check className="h-3.5 w-3.5" />
                          </button>
                          <button type="button" onClick={cancelEditName} className="text-slate-400 hover:text-slate-600 p-0.5">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <>
                          {/* Availability trigger */}
                          <button
                            type="button"
                            data-testid={`court-avail-btn-${num}-${dateStr}`}
                            onClick={() => { setOpenSlot({ court: num, dateStr }); setNewFrom("09:00"); setNewTo("10:00"); }}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium transition-colors hover:brightness-95"
                          >
                            <span className={
                              hasSlots
                                ? "text-emerald-800"
                                : (inheritsFrom === "default"
                                    ? "text-blue-700"
                                    : (inheritsFrom === "global" ? "text-purple-700" : "text-slate-500"))
                            }>
                              {courtLabel(dateStr, num)}
                            </span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full
                              ${hasSlots
                                ? "bg-emerald-200 text-emerald-700"
                                : (inheritsFrom === "default"
                                    ? "bg-blue-100 text-blue-700"
                                    : (inheritsFrom === "global"
                                        ? "bg-purple-100 text-purple-700"
                                        : "bg-slate-200 text-slate-500"))}`}>
                              {hasSlots
                                ? `${slots.length} fascia/e`
                                : (inheritsFrom === "default"
                                    ? "segue default"
                                    : (inheritsFrom === "global" ? "segue globale" : "tutto il giorno"))}
                            </span>
                          </button>
                          {/* Pencil icon — edit name */}
                          <button
                            type="button"
                            data-testid={`court-name-edit-${num}-${dateStr}`}
                            onClick={e => startEditName(dateStr, num, e)}
                            className="pr-2 text-slate-300 hover:text-slate-500 transition-colors"
                            title="Rinomina campo"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* ── Availability dialog ───────────────────────────────────────────── */}
      <Dialog open={!!openSlot} onOpenChange={() => setOpenSlot(null)}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold uppercase">
              {openSlot?.court === -1
                ? "Disponibilità globale — tutti i giorni, tutti i campi"
                : openSlot?.court === 0
                ? `Disponibilità default — ${openSlot ? formatDate(openSlot.dateStr) : ""}`
                : `Orari disponibili — ${openSlot && courtLabel(openSlot.dateStr, openSlot.court)}`
              }
            </DialogTitle>
          </DialogHeader>

          {openSlot && (() => {
            const { court, dateStr } = openSlot;
            const slots = getSlots(court, dateStr);
            const isGlobal  = court === -1;
            const isDefault = court === 0;
            return (
              <div className="space-y-3">
                {!isGlobal && (
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-700 bg-slate-50 rounded-lg px-3 py-2">
                    <Clock className="h-4 w-4 text-slate-400" />
                    {formatDate(dateStr)}
                  </div>
                )}
                <p className="text-xs text-slate-500">
                  {isGlobal ? (
                    <>Fasce di disponibilità <strong>applicate a tutti i campi e tutti i giorni</strong>.
                      Ogni singolo giorno può avere un default specifico e ogni singolo campo può avere
                      i suoi orari personalizzati che sovrascrivono il globale.
                      Se lasci vuoto tutto è usato per tutta la giornata.</>
                  ) : isDefault ? (
                    <>Fasce di disponibilità <strong>applicate a tutti i campi per default</strong> in questa giornata.
                      Ogni singolo campo può avere i suoi orari personalizzati che sovrascrivono il default.
                      Se non aggiungi nessuna fascia il default consente l'uso dei campi per tutta la giornata.</>
                  ) : (
                    <>Aggiungi le fasce orarie in cui il campo <strong>è disponibile</strong> per il torneo. Se non aggiungi nessuna fascia, il campo segue il default del torneo (o è usato tutto il giorno).</>
                  )}
                </p>

                <div className="space-y-1.5">
                  {slots.map((slot, idx) => (
                    <div key={`${slot.from}-${slot.to}`}
                      className={`flex items-center gap-2 border rounded-lg px-3 py-2
                        ${isGlobal ? "bg-purple-50 border-purple-200" : (isDefault ? "bg-blue-50 border-blue-200" : "bg-emerald-50 border-emerald-200")}`}>
                      <Clock className={`h-3.5 w-3.5 flex-shrink-0 ${isGlobal ? "text-purple-500" : (isDefault ? "text-blue-500" : "text-emerald-500")}`} />
                      <span className={`text-sm font-medium flex-1 ${isGlobal ? "text-purple-800" : (isDefault ? "text-blue-800" : "text-emerald-800")}`}>
                        Disponibile: {slot.from} – {slot.to}
                      </span>
                      <button type="button" onClick={() => removeSlot(court, dateStr, idx)}
                        className={`${isGlobal ? "text-purple-400" : (isDefault ? "text-blue-400" : "text-emerald-400")} hover:text-red-500 transition-colors`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {slots.length === 0 && (
                    <p className="text-xs text-slate-500 font-medium text-center py-2 bg-slate-50 rounded-lg border border-slate-200">
                      Nessuna fascia configurata — {isGlobal ? "globale libera tutto il giorno" : (isDefault ? "default tutto il giorno" : "campo disponibile tutto il giorno")}
                    </p>
                  )}
                </div>

                <div className="border-t border-slate-100 pt-3">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                    Aggiungi fascia di disponibilità
                  </p>
                  <div className="flex items-center gap-2">
                    <Input type="time" value={newFrom} onChange={e => setNewFrom(e.target.value)}
                      className="rounded-md h-8 text-xs flex-1" />
                    <span className="text-xs text-slate-400 flex-shrink-0">→</span>
                    <Input type="time" value={newTo} onChange={e => setNewTo(e.target.value)}
                      className="rounded-md h-8 text-xs flex-1" />
                    <Button type="button" onClick={addSlot} size="sm"
                      disabled={newFrom >= newTo}
                      className="bg-green-700 hover:bg-green-800 text-white rounded-md h-8 px-3 gap-1 text-xs flex-shrink-0">
                      <Plus className="h-3 w-3" /> Aggiungi
                    </Button>
                  </div>
                  {newFrom >= newTo && (
                    <p className="text-[10px] text-red-500 mt-1">L'orario di fine deve essere successivo all'inizio.</p>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};
